/**
 * @fileoverview PayDayPuffin Data Processing Agreement registry data.
 *
 * Hand-migrated faithfully from
 * `apps/payday-web/src/portals/marketing-website/pages/DataProcessingAgreementPage.jsx`
 * (15 sections satisfying GDPR Art. 28 requirements).
 */

/** @type {import('../../types.js').LegalDocument} */
export const dpa = Object.freeze({
  appKey: 'payday',
  documentKey: 'dpa',
  frontmatter: {
    title: 'Data Processing Agreement',
    productName: 'PayDayPuffin',
    legalEntity: 'D Town Retail, LLC',
    effectiveDate: '2026-04-11',
    contactEmail: 'hello@paydaypuffin.com',
    canonicalUrl: 'https://paydaypuffin.com/legal/dpa',
    ogImage: 'https://paydaypuffin.com/og-image.jpg',
    governingLaw: 'Oregon',
    description:
      'PayDayPuffin Data Processing Agreement: controller/processor terms, subprocessors, data subject rights, and breach notification.',
  },
  sections: [
    {
      id: 'parties-and-scope',
      title: '1. Parties and Scope',
      order: 1,
      blocks: [
        {
          type: 'paragraph',
          text: 'This Data Processing Agreement ("DPA") is between you, the customer ("Controller"), and {productName} ("Processor"). By using {productName} in a capacity where you upload, transmit, or otherwise provide personal data (as defined by applicable data protection laws), you agree to the terms of this DPA. This DPA supplements and is incorporated by reference into the {productName} Terms of Service and Privacy Policy.',
        },
        {
          type: 'paragraph',
          text: "This DPA applies to all processing of personal data by {productName} on the Controller's behalf in connection with the {productName} payroll software services.",
        },
      ],
    },
    {
      id: 'subject-matter-and-duration',
      title: '2. Subject Matter and Duration of Processing',
      order: 2,
      blocks: [
        {
          type: 'paragraph',
          text: 'Subject matter: Processing personal data necessary to operate payroll software services, including timecard automation, payroll workflow management, employee enrollment, audit logging, and reporting, and transmitting that data to our authorized subprocessor for payroll execution.',
        },
        {
          type: 'paragraph',
          text: 'Duration: Processing continues for as long as the Controller maintains an active {productName} subscription, plus retention periods required by applicable law (typically seven years for payroll and tax records).',
        },
      ],
    },
    {
      id: 'nature-and-purpose',
      title: '3. Nature and Purpose of Processing',
      order: 3,
      blocks: [
        {
          type: 'paragraph',
          text: 'Nature: Collection, storage, transmission to authorized subprocessors, reporting, and deletion of personal data, performed via automated systems.',
        },
        {
          type: 'paragraph',
          text: 'Purpose: To provide the {productName} payroll software services, including (a) transforming timecard data into payroll submissions, (b) transmitting payroll data to our authorized payroll processor for wage execution and tax filing, (c) displaying receipts and pay stubs to the Controller and its employees, and (d) maintaining audit trails and compliance reports.',
        },
      ],
    },
    {
      id: 'types-of-personal-data',
      title: '4. Types of Personal Data',
      order: 4,
      blocks: [
        {
          type: 'paragraph',
          text: "{productName} processes the following categories of personal data on the Controller's behalf:",
        },
        {
          type: 'list',
          items: [
            {
              strong: 'Identity data',
              text: 'Employee name, email address, home address, date of birth, and government-issued identification numbers (including Social Security number and tax identification number).',
            },
            {
              strong: 'Employment data',
              text: 'Job title, hire date, termination date, pay rate, pay schedule, employment status, and employment classification.',
            },
            {
              strong: 'Compensation data',
              text: 'Hours worked, gross pay, net pay, deductions, bonuses, commissions, garnishments, and withheld taxes.',
            },
            {
              strong: 'Banking data',
              text: 'Bank account and routing numbers for direct deposit.',
            },
            {
              strong: 'Operational metadata',
              text: 'Authentication events, sync timestamps, payroll lifecycle transitions, and audit trail entries.',
            },
          ],
        },
      ],
    },
    {
      id: 'categories-of-data-subjects',
      title: '5. Categories of Data Subjects',
      order: 5,
      blocks: [
        {
          type: 'paragraph',
          text: 'The personal data processed by {productName} relates to the following data subjects:',
        },
        {
          type: 'list',
          items: [
            'Employees of the Controller who are enrolled in payroll through {productName}.',
            'Contractors (1099 recipients) of the Controller whose payments are processed through {productName}.',
            "Authorized users of the Controller's {productName} account (such as payroll administrators, managers, and owners).",
          ],
        },
      ],
    },
    {
      id: 'processor-obligations',
      title: '6. Processor Obligations',
      order: 6,
      blocks: [
        { type: 'paragraph', text: '{productName}, as Processor, agrees to:' },
        {
          type: 'list',
          items: [
            "Process personal data only on documented instructions from the Controller, including via the Controller's use of the {productName} services.",
            'Ensure that persons authorized to process personal data are bound by confidentiality obligations.',
            'Implement appropriate technical and organizational measures to ensure a level of security appropriate to the risk, including encryption in transit (TLS 1.2 or higher), encryption at rest for sensitive fields, access controls, and append-only audit logging.',
            'Assist the Controller in responding to data subject requests (access, rectification, erasure, restriction of processing, data portability, and objection).',
            'Assist the Controller with data protection impact assessments (DPIAs) and consultations with supervisory authorities when required.',
            'Delete or return all personal data to the Controller at the end of the provision of services, subject to retention obligations imposed by law.',
            'Make available to the Controller all information necessary to demonstrate compliance with these obligations, and allow for audits on reasonable notice.',
          ],
        },
      ],
    },
    {
      id: 'authorized-subprocessors',
      title: '7. Authorized Subprocessors',
      order: 7,
      blocks: [
        {
          type: 'paragraph',
          text: 'The Controller provides general written authorization for {productName} to engage subprocessors, subject to the conditions below. {productName} maintains a current list of all engaged subprocessors at paydaypuffin.com/subprocessors, including entity name, role, data categories processed, and location.',
        },
        {
          type: 'paragraph',
          text: 'Current subprocessors include: Gusto, Inc. (Delaware C-corporation), for payroll execution including wage payments, tax withholding and filings, direct deposit, and government reporting.',
        },
        {
          type: 'paragraph',
          text: "Notification and objection: {productName} will provide at least thirty (30) days' written notice before engaging any new subprocessor, via posting to the subprocessors page and email notification to the primary account holder. The Controller may object to a new subprocessor on reasonable, objective, and substantive grounds within the notice period. If {productName} cannot reasonably accommodate the objection, the Controller may terminate the subscription without penalty.",
        },
        {
          type: 'paragraph',
          text: 'Subprocessor obligations: {productName} requires every subprocessor to be bound by contractual data protection obligations substantially equivalent to those in this DPA, including confidentiality, security, and data subject rights assistance.',
        },
      ],
    },
    {
      id: 'data-subject-rights',
      title: '8. Data Subject Rights',
      order: 8,
      blocks: [
        {
          type: 'paragraph',
          text: "{productName} will assist the Controller in fulfilling data subject requests. When a data subject submits a request directly to {productName}, we will forward it to the Controller without undue delay and await the Controller's instructions. The Controller remains responsible for responding to data subject requests in accordance with applicable law.",
        },
      ],
    },
    {
      id: 'breach-notification',
      title: '9. Personal Data Breach Notification',
      order: 9,
      blocks: [
        {
          type: 'paragraph',
          text: "{productName} will notify the Controller without undue delay — and in any event within 72 hours of becoming aware — of any personal data breach affecting the Controller's data. The notification will include, to the extent known: the nature of the breach, the categories and approximate number of data subjects and records affected, the likely consequences, and the measures taken or proposed to mitigate possible adverse effects.",
        },
      ],
    },
    {
      id: 'international-data-transfers',
      title: '10. International Data Transfers',
      order: 10,
      blocks: [
        {
          type: 'paragraph',
          text: '{productName} processes data primarily within the United States. If personal data is transferred outside the country of origin, {productName} will ensure that appropriate safeguards are in place, including standard contractual clauses approved by the relevant supervisory authority where required.',
        },
      ],
    },
    {
      id: 'return-or-deletion-of-data',
      title: '11. Return or Deletion of Data',
      order: 11,
      blocks: [
        {
          type: 'paragraph',
          text: 'Upon termination of the subscription, the Controller may request the return of all personal data in a machine-readable format within 30 days. After the 30-day window, {productName} will delete all personal data from active systems, subject to (a) backup rotation windows (typically 90 days), and (b) retention obligations imposed by applicable tax and employment law (typically seven years for payroll and tax records).',
        },
      ],
    },
    {
      id: 'audit-rights',
      title: '12. Audit Rights',
      order: 12,
      blocks: [
        {
          type: 'paragraph',
          text: "The Controller may, upon reasonable notice and during normal business hours, audit {productName}'s compliance with this DPA no more than once per calendar year (unless required by a supervisory authority or triggered by a personal data breach). Audits will be conducted at the Controller's expense and subject to reasonable confidentiality obligations.",
        },
      ],
    },
    {
      id: 'requesting-a-signed-dpa',
      title: '13. Requesting a Signed DPA',
      order: 13,
      blocks: [
        {
          type: 'paragraph',
          text: 'This DPA applies to every {productName} customer by reference. If your organization requires a formally signed counterpart DPA for your internal records or compliance program, please contact {contactEmail} with your legal entity name and preferred signing method. We respond to DPA requests within five business days.',
        },
      ],
    },
    {
      id: 'governing-law-and-jurisdiction',
      title: '14. Governing Law and Jurisdiction',
      order: 14,
      blocks: [
        {
          type: 'paragraph',
          text: 'This DPA is governed by the laws of the State of {governingLaw}, without regard to its conflict of law provisions. Any dispute arising out of or relating to this DPA will be subject to the exclusive jurisdiction of the state and federal courts located in {governingLaw}.',
        },
      ],
    },
    {
      id: 'contact',
      title: '15. Contact',
      order: 15,
      blocks: [
        {
          type: 'paragraph',
          text: 'Questions about this DPA, subprocessor list, or our data processing practices? Contact us at {contactEmail}. We aim to respond within two business days.',
        },
        { type: 'contact' },
      ],
    },
  ],
});
